import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapDownIcon } from '~/assets/icons';
import { MapLayerOption } from '../hooks/useMapSettings';

const LAYER_OPTIONS: { value: MapLayerOption; label: string }[] = [
  { value: 'streets', label: 'Google Streets' },
  { value: 'hybrid', label: 'Google Hybrid' },
];

interface Props {
  layer: MapLayerOption;
  brightness: number;
  onLayerChange: (l: MapLayerOption) => void;
  onBrightnessChange: (v: number) => void;
}

function MapSettingsPanel({ layer, brightness, onLayerChange, onBrightnessChange }: Props) {
  const { t } = useTranslation('admin/map');
  const [layerOpen, setLayerOpen] = useState(false);
  const selectedLabel = LAYER_OPTIONS.find((o) => o.value === layer)?.label ?? '';

  return (
    <div className='absolute top-0 right-full mr-2 z-20 bg-white rounded-lg shadow-lg p-4 w-60 text-sm border border-gray-200'>
      <p className='font-bold text-gray-800 mb-3'>{t('settingsPanel.title')}</p>

      <div className='mb-4'>
        <p className='text-gray-600 mb-1.5'>{t('settingsPanel.chooseLayer')}:</p>
        <div className='relative'>
          <button
            type='button'
            onClick={() => setLayerOpen((p) => !p)}
            className='w-full h-9 flex items-center justify-between gap-2 border border-gray-300 rounded-lg px-3 text-sm text-gray-800 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400'
          >
            <span>{selectedLabel}</span>
            <MapDownIcon className='w-4 h-4 flex-shrink-0 opacity-60' />
          </button>
          {layerOpen && (
            <div className='absolute left-0 right-0 mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden'>
              {LAYER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type='button'
                  onClick={() => {
                    onLayerChange(opt.value);
                    setLayerOpen(false);
                  }}
                  className={`w-full text-left text-sm px-3 py-2 transition-colors hover:bg-[#F3F6F9] ${
                    opt.value === layer ? 'text-[#0E2F58] font-medium' : 'text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <p className='text-gray-600 mb-1.5'>{t('settingsPanel.brightness')}:</p>
        <input
          type='range'
          min={50}
          max={150}
          value={brightness}
          onChange={(e) => onBrightnessChange(Number(e.target.value))}
          className='w-full accent-[#2E89C8] cursor-pointer'
        />
      </div>
    </div>
  );
}

export default MapSettingsPanel;
